import { useCallback, useMemo, useRef, useState } from 'react';

import { localNow } from '@/utils/local-now';

export const SAMPLE_COUNT = 5;

export interface TimeSample {
  sentAt: number;
  receivedAt: number;
  serverTime: number;
}

interface TimeMessage {
  type: 'time';
  client_sent_at: number;
  server_time: number;
}

export interface ServerClock {
  offset: number | null;
  serverNow: () => number | null;
  sample: (perform: (action: string, data: object) => void) => void;
  receive: (
    message: Pick<TimeMessage, 'client_sent_at' | 'server_time'>,
  ) => void;
}

export const computeOffset = (samples: TimeSample[]): number | null => {
  if (samples.length === 0) {
    return null;
  }

  const best = samples.reduce((a, b) =>
    b.receivedAt - b.sentAt < a.receivedAt - a.sentAt ? b : a,
  );

  return best.serverTime - (best.sentAt + best.receivedAt) / 2;
};

export const isTimeMessage = (data: unknown): data is TimeMessage =>
  typeof data === 'object' &&
  data !== null &&
  (data as { type?: unknown }).type === 'time' &&
  typeof (data as { client_sent_at?: unknown }).client_sent_at === 'number' &&
  typeof (data as { server_time?: unknown }).server_time === 'number';

export const useServerClock = (): ServerClock => {
  const pending = useRef(new Set<number>());
  const samples = useRef<TimeSample[]>([]);
  const [offset, setOffset] = useState<number | null>(null);

  const sample = useCallback(
    (perform: (action: string, data: object) => void) => {
      samples.current = [];

      for (let i = 0; i < SAMPLE_COUNT; i += 1) {
        // Keeps requests fired within the same millisecond distinguishable
        // when they echo back.
        const sentAt = localNow() + i / 1000;

        pending.current.add(sentAt);
        perform('time', { client_sent_at: sentAt });
      }
    },
    [],
  );

  const receive = useCallback(
    (message: Pick<TimeMessage, 'client_sent_at' | 'server_time'>) => {
      if (!pending.current.delete(message.client_sent_at)) {
        return;
      }

      samples.current.push({
        sentAt: message.client_sent_at,
        receivedAt: localNow(),
        serverTime: message.server_time,
      });
      setOffset(computeOffset(samples.current));
    },
    [],
  );

  const serverNow = useCallback(
    () => (offset === null ? null : localNow() + offset),
    [offset],
  );

  return useMemo(
    () => ({ offset, serverNow, sample, receive }),
    [offset, serverNow, sample, receive],
  );
};
